"use client";

import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { OptionPair } from "@erp/shared-libs/common";
import { cn } from "@erp/shared-libs/utils";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "motion/react";
import { FC, Fragment, memo, PropsWithChildren, useCallback, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

interface BaseOptionMenuProps {
  options: OptionPair[];
  isLoading?: boolean;
  /**
   * 觸發鈕外觀
   * - default：藥丸下拉鈕（既有樣式）
   * - ai：扁平 pill 篩選鈕（對應設計稿 .sa-filter-pill，hover 變主色、有值時換主色細框）
   */
  variant?: "default" | "ai";
}

interface SingleOptionMenuProps extends BaseOptionMenuProps {
  id: number | string | undefined;
  ids?: never;
  mutiple?: never;
  onChange: (status: number | string) => void;
}

interface MultipleOptionMenuProps<T extends number | string> extends BaseOptionMenuProps {
  id?: never;
  ids: T[];
  mutiple: true;
  onChange: (ids: T[]) => void;
}

type OptionMenuProps<T extends number | string = number | string> =
  | SingleOptionMenuProps
  | MultipleOptionMenuProps<T>;

export function OptionMenu<T extends number | string = number | string>({
  children,
  isLoading,
  variant = "default",
  ...props
}: OptionMenuProps<T> & PropsWithChildren) {
  // =====================================================
  // Variables
  // =====================================================
  /**
   * 是否已選取值（單選看 id、多選看 ids 長度）；ai pill 用來切換「有值」外觀
   */
  const hasValue = props.mutiple ? props.ids.length > 0 : props.id !== undefined;

  /**
   * 多選時已選數量（單選不顯示計數 badge）
   */
  const selectedCount = props.mutiple ? props.ids.length : 0;

  // =====================================================
  // Render
  // =====================================================
  return (
    <DropdownMenu>
      {/* Trigger */}
      <DropdownMenuTrigger asChild>
        {variant === "ai" ? (
          <Button
            variant="aiFilter"
            active={hasValue}
            isLoading={isLoading}
            className="h-10.5 text-dense group gap-2.5 px-3.5 font-medium"
          >
            <div className="flex items-center gap-2.5">
              {children}

              {/* 已選數量 badge：多選且有值時滑入（淡入 + 由左滑進 + 微縮放） */}
              {hasValue && selectedCount > 0 ? (
                <span
                  className="bg-primary text-primary-foreground animate-in fade-in zoom-in-75 slide-in-from-left-1 h-4.5
                    min-w-4.5 grid place-items-center rounded-full px-1 text-[10px] font-bold duration-200"
                >
                  {selectedCount}
                </span>
              ) : null}
            </div>

            <ChevronDown className={cn("text-muted-foreground", hasValue && "text-primary")} />
          </Button>
        ) : (
          <Button variant="dropdown" size="dropdown" className="self-stretch" isLoading={isLoading}>
            <div className="flex items-center justify-center">
              {children}
              <ChevronDown className="ml-3 dark:text-white" />
            </div>
          </Button>
        )}
      </DropdownMenuTrigger>
      {/* Content：min-width 對齊 trigger 寬度（Radix 提供的 CSS 變數，自動偵測） */}
      <DropdownMenuContent
        className={cn(
          "min-w-(--radix-dropdown-menu-trigger-width) relative",
          variant === "ai" && "border-border-lighter"
        )}
      >
        <OptionMenuContent {...(props as OptionMenuProps)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const OptionMenuContent: FC<OptionMenuProps> = memo(({ mutiple, options, id, ids, onChange }) => {
  // =====================================================
  // Hooks
  // =====================================================
  /**
   * 是否顯示底部漸層遮罩（捲到頂時顯示，提示下方還有選項）
   */
  const [showGradient, setShowGradient] = useState(true);

  // =====================================================
  // Variables
  // =====================================================
  /**
   * 排序後的選項（disabled 沉到最後）
   */
  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) => {
        if (a.disabled === b.disabled) return 0;
        if (a.disabled) return 1;
        return -1;
      }),
    [options]
  );

  // =====================================================
  // Functions
  // =====================================================
  /**
   * 多選：切換某個選項的勾選狀態並回拋新的 ids 陣列
   */
  const handleCheck = useCallback(
    (value: number | string) => () => {
      if (!mutiple) return;

      const typedIds = ids as (number | string)[];
      const typedOnChange = onChange as (id: (number | string)[]) => void;
      typedOnChange(
        typedIds.includes(value) ? typedIds.filter((x) => x !== value) : [...typedIds, value]
      );
    },
    [ids, mutiple, onChange]
  );

  /**
   * 依捲動位置切換底部漸層遮罩顯示
   */
  const handleOnScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setShowGradient(e.currentTarget.scrollTop === 0);
  }, []);

  /**
   * 單選：直接回拋選中的 value
   */
  const handleSingleChange = useCallback(
    (value: number) => () => {
      if (mutiple) return;

      onChange(value);
    },
    [mutiple, onChange]
  );

  // =====================================================
  // Render
  // =====================================================
  return (
    <>
      <AnimatePresence>
        {showGradient ? (
          <motion.div
            ref={(ref) => {
              const peer = ref?.nextSibling as HTMLDivElement;
              if (peer && peer.scrollHeight > peer.clientHeight) {
                ref?.classList.remove("hidden");
              }
            }}
            className="from-primary-50 dark:from-primary-100 bg-linear-to-t pointer-events-none absolute bottom-0 right-0
              z-20 hidden h-32 w-full to-transparent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          />
        ) : null}
      </AnimatePresence>
      <div className="max-h-60 flex-1 overflow-y-auto overflow-x-hidden" onScroll={handleOnScroll}>
        {sortedOptions?.map((option, index) =>
          mutiple ? (
            <Fragment key={option.value}>
              <DropdownMenuItem onSelect={(e) => e.preventDefault()} asChild>
                <label className="flex items-center">
                  <Checkbox
                    onCheckedChange={handleCheck(option.value)}
                    checked={(ids as (number | string)[]).includes(option.value)}
                    disabled={option.disabled}
                  />
                  <p className="ml-2">{option.label}</p>
                </label>
              </DropdownMenuItem>

              {/* {index < options.length - 1 ? <DropdownMenuSeparator /> : null} */}
            </Fragment>
          ) : (
            <Fragment key={option.value}>
              <DropdownMenuItem
                data-active={id === option.value}
                onClick={handleSingleChange(option.value as number)}
                className="data-[active=true]:bg-primary data-[active=true]:text-primary-foreground justify-center"
                disabled={option.disabled}
              >
                {option.labelNode || option.label}
              </DropdownMenuItem>

              {index < options.length - 1 ? <DropdownMenuSeparator /> : null}
            </Fragment>
          )
        )}
      </div>
    </>
  );
});

OptionMenuContent.displayName = "OptionMenuContent";
